import { z } from "zod";

import { EDDDisplayCodes } from "@/fhir/enums/quilted-health";

export const EddAnswersForm = z.object({
  eddDisplay: z
    .string()
    .or(
      z.object({
        system: z.string().optional(),
        code: z.string().optional(),
        display: z.string().optional(),
      }),
    )
    .optional(),
  isPregnant: z.boolean().optional(),
  lmpDate: z.date().optional(),
  lmpCertain: z.string().optional(),
  lmpEddDate: z.date().optional(),
  ultrasoundDate: z.date().optional(),
  ultrasoundGestationalAgeWeeks: z.number().nonnegative().optional(),
  ultrasoundGestationalAgeDays: z.number().nonnegative().optional(),
  ultrasoundEddDate: z.date().optional(),
  customEddDate: z.date().optional(),
});

export type EddAnswersFormType = z.infer<typeof EddAnswersForm>;

/**
 * Zod superRefine refinement that enforces a required date field based on the
 * selected EDD display type. Attach to any schema whose data shape includes
 * `eddDisplay`, `lmpDate`, `ultrasoundDate`, and `customEddDate`.
 *
 * @param data - Form data record passed by Zod during refinement
 * @param ctx - Zod refinement context used to attach field-level issues
 */
export function eddDisplayDateRefinement(
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
): void {
  const rawEddDisplay = data.eddDisplay;
  const displayCode =
    typeof rawEddDisplay === "string"
      ? rawEddDisplay
      : (rawEddDisplay as { code?: string } | undefined)?.code;

  if (!displayCode) return;

  if (displayCode === EDDDisplayCodes.LMP) {
    if (!data.lmpDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "LMP date is required when LMP EDD is selected",
        path: ["lmpDate"],
      });
    }
  } else if (displayCode === EDDDisplayCodes.Ultrasound) {
    if (!data.ultrasoundDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ultrasound date is required when Ultrasound EDD is selected",
        path: ["ultrasoundDate"],
      });
    }
  } else if (displayCode === EDDDisplayCodes.Custom) {
    if (!data.customEddDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Custom EDD date is required when Custom EDD is selected",
        path: ["customEddDate"],
      });
    }
  }
}
